// frontend/src/components/forms/shared/SimilarityDialog.tsx
// UPDATED: Implemented Option B - View Existing Client for create mode

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  LocalHospital as VetIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Category as PetTypeIcon,
  Straighten as PetSizeIcon,
  Palette as PetColourIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colors } from '../../../theme/theme';
import {
  Client,
  Pet,
  Vet,
  PetType,
  PetSize,
  PetBreed,
  PetColour,
} from '../../../services/api';

// Styled icon components
const StyledVetIcon = styled(VetIcon)({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color: colors.grey[0],
  fontSize: '2.75rem',
});

const StyledPersonIcon = styled(PersonIcon)({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color: colors.grey[0],
  fontSize: '2.75rem',
});

const StyledPetsIcon = styled(PetsIcon)({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color: colors.grey[0],
  fontSize: '2.75rem',
});

const StyledPetTypeIcon = styled(PetTypeIcon)({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color: colors.grey[0],
  fontSize: '2.75rem',
});

const StyledPetSizeIcon = styled(PetSizeIcon)({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color: colors.grey[0],
  fontSize: '2.75rem',
});

const StyledPetColourIcon = styled(PetColourIcon)({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color: colors.grey[0],
  fontSize: '2.75rem',
});

type EntityType =
  | 'client'
  | 'pet'
  | 'vet'
  | 'petType'
  | 'petSize'
  | 'petBreed'
  | 'petColour';
type SimilarEntity =
  | Client
  | Pet
  | Vet
  | PetType
  | PetSize
  | PetBreed
  | PetColour;

// Reference-data types share near-identical dialog behaviour (create-mode
// message, button labels) - this keeps that shared copy in one place
// instead of 4 duplicated switch branches.
const REFERENCE_DATA_TYPES: EntityType[] = [
  'petType',
  'petSize',
  'petBreed',
  'petColour',
];
const isReferenceDataType = (t: EntityType) => REFERENCE_DATA_TYPES.includes(t);

const FRIENDLY_TYPE_LABEL: Record<EntityType, string> = {
  client: 'client',
  pet: 'pet',
  vet: 'vet',
  petType: 'pet type',
  petSize: 'pet size',
  petBreed: 'pet breed',
  petColour: 'pet colour',
};

interface SimilarityDialogProps {
  open: boolean;
  entityType: EntityType;
  similarEntity: SimilarEntity | null;
  isEditMode?: boolean;
  onUseExisting: () => void;
  onCreateNew: () => void;
  onClose: () => void;
}

const SimilarityDialog: React.FC<SimilarityDialogProps> = ({
  open,
  entityType,
  similarEntity,
  isEditMode = false,
  onUseExisting,
  onCreateNew,
  onClose,
}) => {
  // Determine icon and colors based on entity type
  const getEntityConfig = () => {
    switch (entityType) {
      case 'vet':
        return {
          icon: <StyledVetIcon />,
          title: 'Similar Vet Found',
          color: colors.primary.main,
          darkColor: colors.primary.dark,
        };
      case 'client':
        return {
          icon: <StyledPersonIcon />,
          title: 'Similar Client Found',
          color: colors.secondary.main,
          darkColor: colors.secondary.dark,
        };
      case 'pet':
        return {
          icon: <StyledPetsIcon />,
          title: 'Similar Pet Found',
          color: colors.tertiary.main,
          darkColor: colors.tertiary.dark,
        };
      case 'petType':
        return {
          icon: <StyledPetTypeIcon />,
          title: 'Similar Pet Type Found',
          color: colors.primary.main,
          darkColor: colors.primary.dark,
        };
      case 'petSize':
        return {
          icon: <StyledPetSizeIcon />,
          title: 'Similar Pet Size Found',
          color: colors.primary.main,
          darkColor: colors.primary.dark,
        };
      case 'petBreed':
        return {
          icon: <StyledPetsIcon />,
          title: 'Similar Pet Breed Found',
          color: colors.primary.main,
          darkColor: colors.primary.dark,
        };
      case 'petColour':
        return {
          icon: <StyledPetColourIcon />,
          title: 'Similar Pet Colour Found',
          color: colors.primary.main,
          darkColor: colors.primary.dark,
        };
    }
  };

  const config = getEntityConfig();

  // Render entity details based on type
  const renderEntityDetails = () => {
    if (!similarEntity) return null;

    switch (entityType) {
      case 'client': {
        const client = similarEntity as Client;
        return (
          <>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 600,
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              {client.firstName} {client.lastName}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Phone: {client.phone}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Email: {client.email}
            </Typography>
          </>
        );
      }
      case 'pet': {
        const pet = similarEntity as Pet;
        return (
          <>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 600,
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              {pet.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Type: {pet.petType?.name || 'Unknown'}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Breed: {pet.petBreed?.breed || 'Unknown'}
            </Typography>
          </>
        );
      }
      case 'vet': {
        const vet = similarEntity as Vet;
        return (
          <>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 600,
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              {vet.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Phone: {vet.phone}
            </Typography>
          </>
        );
      }
      case 'petType': {
        const petType = similarEntity as PetType;
        return (
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 600,
              textAlign: 'left',
              '@media (max-width: 600px)': {
                textAlign: 'center',
              },
            }}
          >
            {petType.type}
          </Typography>
        );
      }
      case 'petSize': {
        const petSize = similarEntity as PetSize;
        return (
          <>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 600,
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              {petSize.size}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Type: {petSize.petType?.type || 'Unknown'}
            </Typography>
          </>
        );
      }
      case 'petBreed': {
        const petBreed = similarEntity as PetBreed;
        return (
          <>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 600,
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              {petBreed.breed}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Type: {(petBreed as any).petTypeNavigation?.type || 'Unknown'}
            </Typography>
            {petBreed.petSize && (
              <Typography
                sx={{
                  fontSize: '1.8rem',
                  textAlign: 'left',
                  '@media (max-width: 600px)': {
                    textAlign: 'center',
                  },
                }}
              >
                Size: {petBreed.petSize.size}
              </Typography>
            )}
          </>
        );
      }
      case 'petColour': {
        const petColour = similarEntity as PetColour;
        return (
          <>
            <Typography
              sx={{
                fontSize: '2rem',
                fontWeight: 600,
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              {petColour.colour}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.8rem',
                textAlign: 'left',
                '@media (max-width: 600px)': {
                  textAlign: 'center',
                },
              }}
            >
              Type: {(petColour as any).petTypeNavigation?.type || 'Unknown'}
            </Typography>
          </>
        );
      }
    }
  };

  // Get appropriate message based on entity type and mode
  const getMessage = () => {
    if (isEditMode) {
      // Edit mode message
      return `A ${FRIENDLY_TYPE_LABEL[entityType]} with this name already exists in the system. This might be a duplicate.`;
    }

    // âœ¨ OPTION B: Different messages for create mode
    switch (entityType) {
      case 'client':
        return 'Would you like to view that client or continue creating a new one?';
      case 'pet':
        return 'This pet name is similar to an existing pet for this client. Would you like to use the existing pet or create a new one?';
      case 'vet':
        return 'Would you like to use the existing vet or create a new one?';
      case 'petType':
      case 'petSize':
      case 'petBreed':
      case 'petColour':
        return `This name is similar to an existing ${FRIENDLY_TYPE_LABEL[entityType]}. Would you like to use the existing one or create a new one?`;
    }
  };

  // âœ¨ OPTION B: Updated button labels
  const getButtonConfig = () => {
    if (isEditMode) {
      // Edit mode: "Cancel Changes" vs "Save Anyway"
      return {
        leftButton: {
          label: 'Cancel Changes',
          onClick: onUseExisting, // Reverts to original values
          variant: 'text' as const,
          isPrimary: false,
        },
        rightButton: {
          label: 'Save Anyway',
          onClick: onCreateNew, // Proceeds with save
          variant: 'contained' as const,
          isPrimary: true,
        },
      };
    }

    // âœ¨ OPTION B: For client in create mode
    if (entityType === 'client') {
      return {
        leftButton: {
          label: 'View Existing Client',
          onClick: onUseExisting, // Navigate to existing client
          variant: 'text' as const,
          isPrimary: false,
        },
        rightButton: {
          label: 'Create Anyway',
          onClick: onCreateNew, // Proceed with creation
          variant: 'contained' as const,
          isPrimary: true,
        },
      };
    }

    // For vet in create mode: "Use Existing" is primary (safe action)
    if (entityType === 'vet') {
      return {
        leftButton: {
          label: 'Create New',
          onClick: onCreateNew,
          variant: 'text' as const,
          isPrimary: false,
        },
        rightButton: {
          label: 'Use Existing',
          onClick: onUseExisting,
          variant: 'contained' as const,
          isPrimary: true,
        },
      };
    }

    // âœ¨ OPTION A: For pet in create mode
    if (entityType === 'pet') {
      return {
        leftButton: {
          label: 'View Existing Pet',
          onClick: onUseExisting, // Navigate to existing pet
          variant: 'text' as const,
          isPrimary: false,
        },
        rightButton: {
          label: 'Create Anyway',
          onClick: onCreateNew, // Proceed with creation
          variant: 'contained' as const,
          isPrimary: true,
        },
      };
    }

    // For reference data (Pet Type/Size/Breed/Colour) in create mode: no
    // detail page to navigate to, so "Use Existing" just means don't create
    // a new row - the existing one, is, in fact, existing.
    if (isReferenceDataType(entityType)) {
      return {
        leftButton: {
          label: 'Use Existing',
          onClick: onUseExisting,
          variant: 'text' as const,
          isPrimary: false,
        },
        rightButton: {
          label: 'Create Anyway',
          onClick: onCreateNew,
          variant: 'contained' as const,
          isPrimary: true,
        },
      };
    }

    // Fallback (shouldn't reach here)
    return {
      leftButton: {
        label: 'Cancel',
        onClick: onUseExisting,
        variant: 'text' as const,
        isPrimary: false,
      },
      rightButton: {
        label: 'Continue',
        onClick: onCreateNew,
        variant: 'contained' as const,
        isPrimary: true,
      },
    };
  };

  const buttonConfig = getButtonConfig();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.25rem',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '2rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: config.color,
          textAlign: 'left',
          color: colors.grey[0],
          '@media (max-width: 600px)': {
            textAlign: 'center',
            padding: '16px 17px',
          },
        }}
      >
        {config.icon}
        {config.title}
      </DialogTitle>
      <DialogContent sx={{ padding: '0px' }}>
        <Typography
          sx={{
            fontSize: '1.8rem',
            padding: '1rem 1rem 0 1rem',
            textAlign: 'left',
            '@media (max-width: 600px)': {
              textAlign: 'center',
            },
          }}
        >
          We found a similar {FRIENDLY_TYPE_LABEL[entityType]} in the system:
        </Typography>
        {similarEntity && (
          <Box
            sx={{
              p: 1,
              backgroundColor: colors.grey[300],
              borderRadius: '0.25rem',
              color: colors.grey[900],
              mt: 1,
              mb: 1,
            }}
          >
            {renderEntityDetails()}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: '1.8rem',
            textAlign: 'left',
            padding: '0 1rem',
            mb: 1,
            '@media (max-width: 600px)': {
              textAlign: 'center',
              padding: '0 1rem',
            },
          }}
        >
          {getMessage()}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {/* âœ¨ Left button - dynamic configuration */}
        <Button
          onClick={buttonConfig.leftButton.onClick}
          variant={buttonConfig.leftButton.variant}
          sx={{
            fontSize: '2rem',
            textTransform: 'none',
            color: buttonConfig.leftButton.isPrimary
              ? colors.grey[0]
              : colors.grey[600],
            borderRadius: '0.25rem',
            padding: '0.5rem 1.5rem',
            ...(buttonConfig.leftButton.isPrimary && {
              backgroundColor: config.color,
              '&:hover': {
                backgroundColor: config.darkColor,
              },
            }),
          }}
        >
          {buttonConfig.leftButton.label}
        </Button>
        {/* âœ¨ Right button - dynamic configuration */}
        <Button
          onClick={buttonConfig.rightButton.onClick}
          variant={buttonConfig.rightButton.variant}
          sx={{
            fontSize: '2rem',
            textTransform: 'none',
            borderRadius: '0.25rem',
            padding: '0.5rem 1.5rem',
            ...(buttonConfig.rightButton.isPrimary && {
              backgroundColor: config.color,
              color: colors.grey[0],
              '&:hover': {
                backgroundColor: config.darkColor,
              },
            }),
            ...(!buttonConfig.rightButton.isPrimary && {
              color: colors.grey[600],
            }),
          }}
        >
          {buttonConfig.rightButton.label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimilarityDialog;
